import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Search,
  Target,
  TrendingUp,
  CheckCircle,
  Loader2,
  Users,
  BarChart3,
  Star,
  ChevronRight,
  Download,
} from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useAllIndividualOKRs } from '@/hooks/queries/useOKRQueries'
import { getAllIndividualOKRs } from '@/api/performance'
import { useTenant } from '@/hooks/useTenant'
import { MetricCard } from '@/components/MetricCard'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/EmptyState'
import { SkeletonCard } from '@/components/common/SkeletonCard'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { exportObjetivosToExcel } from './utils/exportToExcel'

// ─── Helpers ─────────────────────────────────────────────────

const getInitials = (name: string) => {
  if (!name) return '?'
  const parts = name.split(' ').filter(p => p.length > 0)
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`
  if (parts.length === 1) return `${parts[0][0]}${parts[0][1] || ''}`
  return '?'
}

const getScoreStyle = (score: number | null | undefined): { color: string; label: string } => {
  if (score === null || score === undefined) return { color: 'text-gray-400', label: 'Não avaliado' }
  if (score < 1.5) return { color: 'text-red-400', label: 'Não atingiu' }
  if (score < 2.5) return { color: 'text-orange-400', label: 'Parcialmente' }
  if (score < 3.5) return { color: 'text-amber-400', label: 'Atingido' }
  if (score < 4.5) return { color: 'text-emerald-400', label: 'Superou' }
  return { color: 'text-violet-400', label: 'Excepcional' }
}

const getProgressColor = (progress: number) => {
  if (progress >= 80) return 'bg-emerald-500'
  if (progress >= 50) return 'bg-amber-500'
  if (progress >= 25) return 'bg-orange-500'
  return 'bg-red-500'
}

// ─── Types ───────────────────────────────────────────────────

interface EmployeeGroup {
  ownerId: string
  ownerName: string
  okrs: any[]
  total: number
  active: number
  completed: number
  draft: number
  avgProgress: number
  avgScore: number | null
  scoredCount: number
  totalKRs: number
  scoredKRs: number
}

// ─── Component ───────────────────────────────────────────────

function ObjetivosIndividuaisPage() {
  const { tenantDomain } = useTenant()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Pagination state (accumulation pattern)
  const [allItems, setAllItems] = useState<any[]>([])
  const [nextToken, setNextToken] = useState<string | undefined>()
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Export state
  const [isExporting, setIsExporting] = useState(false)

  // Debounce search
  const searchTimeoutRef = React.useRef<NodeJS.Timeout>()
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(value), 300)
  }

  const { data, isLoading, isError } = useAllIndividualOKRs({
    search: debouncedSearch || undefined,
    limit: 100,
  })

  // Sync initial query data into accumulated state
  useEffect(() => {
    if (data?.items) {
      setAllItems(data.items)
      setNextToken(data.pagination?.nextToken)
    }
  }, [data])

  // Auto-exhaust pagination to get all OKRs for grouping
  useEffect(() => {
    if (!nextToken || !tenantDomain || isLoadingMore) return

    const exhaustPages = async () => {
      setIsLoadingMore(true)
      let currentToken: string | undefined = nextToken
      let accumulated = [...allItems]

      while (currentToken) {
        try {
          const result = await getAllIndividualOKRs(tenantDomain, {
            search: debouncedSearch || undefined,
            limit: 100,
            nextToken: currentToken,
          })
          accumulated = [...accumulated, ...result.items]
          currentToken = result.pagination?.nextToken
        } catch {
          break
        }
      }

      setAllItems(accumulated)
      setNextToken(undefined)
      setIsLoadingMore(false)
    }

    exhaustPages()
  }, [nextToken, tenantDomain]) // eslint-disable-line react-hooks/exhaustive-deps

  // Group OKRs by employee
  const employees = useMemo<EmployeeGroup[]>(() => {
    const grouped = new Map<string, { ownerName: string; okrs: any[] }>()

    for (const okr of allItems) {
      const key = okr.ownerId
      if (!key) continue
      if (!grouped.has(key)) {
        grouped.set(key, { ownerName: okr.ownerName || '—', okrs: [] })
      }
      grouped.get(key)!.okrs.push(okr)
    }

    return Array.from(grouped.entries())
      .map(([ownerId, data]) => {
        const scores = data.okrs
          .map((o: any) => o.objectiveScore)
          .filter((s: any) => s != null && s !== undefined) as number[]
        const avgScore = scores.length > 0
          ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
          : null

        return {
          ownerId,
          ownerName: data.ownerName,
          okrs: data.okrs,
          total: data.okrs.length,
          active: data.okrs.filter((o: any) => o.status === 'active').length,
          completed: data.okrs.filter((o: any) => o.status === 'completed').length,
          draft: data.okrs.filter((o: any) => o.status === 'draft').length,
          avgProgress: Math.round(
            data.okrs.reduce((s: number, o: any) => s + (o.progress || 0), 0) / data.okrs.length
          ),
          avgScore,
          scoredCount: data.okrs.filter((o: any) => o.scoredKRs > 0).length,
          totalKRs: data.okrs.reduce((s: number, o: any) => s + (o.totalKRs || 0), 0),
          scoredKRs: data.okrs.reduce((s: number, o: any) => s + (o.scoredKRs || 0), 0),
        }
      })
      .sort((a, b) => a.ownerName.localeCompare(b.ownerName, 'pt-BR'))
  }, [allItems])

  // Global stats
  const stats = useMemo(() => {
    const totalEmployees = employees.length
    const totalOKRs = allItems.length
    const activeOKRs = allItems.filter((o: any) => o.status === 'active').length
    const allProgress = allItems.map((o: any) => o.progress || 0)
    const avgProgress = allProgress.length > 0
      ? Math.round(allProgress.reduce((a: number, b: number) => a + b, 0) / allProgress.length)
      : 0
    return { totalEmployees, totalOKRs, activeOKRs, avgProgress }
  }, [employees, allItems])

  // Export handler
  const handleExport = useCallback(async () => {
    if (!tenantDomain || isExporting) return
    setIsExporting(true)
    try {
      await exportObjetivosToExcel({ tenantDomain, okrs: allItems, statusFilter: 'all' })
    } catch (err) {
      console.error('Error exporting to Excel:', err)
    } finally {
      setIsExporting(false)
    }
  }, [tenantDomain, isExporting, allItems])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2">
            Objetivos Individuais
          </h1>
          <p className="font-body text-brand-gray">
            Visão administrativa dos objetivos por colaborador
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExport}
          disabled={isExporting || isLoading || allItems.length === 0}
          className="border border-brand-gray/20 self-start sm:self-center"
        >
          {isExporting ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Exportando...</>
          ) : (
            <><Download className="w-4 h-4 mr-2" /> Exportar Excel</>
          )}
        </Button>
      </div>

      {/* Stats Cards */}
      {!isLoading && employees.length > 0 && (
        <div className="dashboard-grid lg:grid-cols-4">
          <MetricCard
            title="Colaboradores"
            value={stats.totalEmployees}
            icon={Users}
            trend="neutral"
          />
          <MetricCard
            title="Total de Objetivos"
            value={stats.totalOKRs}
            icon={Target}
            trend="neutral"
          />
          <MetricCard
            title="Objetivos Ativos"
            value={stats.activeOKRs}
            icon={TrendingUp}
            trend="neutral"
          />
          <MetricCard
            title="Progresso Médio"
            value={`${stats.avgProgress}%`}
            icon={BarChart3}
            trend="neutral"
          />
        </div>
      )}

      {/* Search */}
      <Card className="p-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray" />
          <input
            type="text"
            placeholder="Buscar por colaborador..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-brand-surface border border-brand-border rounded-lg text-white placeholder-brand-gray focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
          />
        </div>
      </Card>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          <SkeletonCard count={6} />
        </div>
      ) : isError ? (
        <Card className="p-12 text-center">
          <p className="text-red-400">Erro ao carregar objetivos. Tente novamente.</p>
        </Card>
      ) : employees.length === 0 ? (
        <EmptyState
          icon={Target}
          title="Nenhum colaborador encontrado"
          description="Ajuste a busca para ver mais resultados"
        />
      ) : (
        <>
          {isLoadingMore && (
            <div className="flex items-center gap-2 py-2 text-sm text-brand-gray">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Carregando todos os objetivos...</span>
            </div>
          )}

          <div className="grid gap-4">
            {employees.map((emp) => {
              const progressColor = getProgressColor(emp.avgProgress)
              const scoreStyle = getScoreStyle(emp.avgScore)

              // Summary parts
              const parts: string[] = []
              parts.push(`${emp.total} objetivo${emp.total !== 1 ? 's' : ''}`)
              if (emp.active > 0) parts.push(`${emp.active} ativo${emp.active !== 1 ? 's' : ''}`)
              if (emp.completed > 0) parts.push(`${emp.completed} concluído${emp.completed !== 1 ? 's' : ''}`)
              if (emp.draft > 0) parts.push(`${emp.draft} rascunho${emp.draft !== 1 ? 's' : ''}`)

              return (
                <Card
                  key={emp.ownerId}
                  className="card-hover p-6 cursor-pointer"
                  onClick={() => navigate({
                    to: '/performance/objetivos-individuais/detail',
                    search: { ownerId: emp.ownerId, ownerName: emp.ownerName },
                  })}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {/* Avatar */}
                      <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-brand-dark">
                          {getInitials(emp.ownerName).toUpperCase()}
                        </span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display text-lg font-semibold text-white truncate">
                          {emp.ownerName}
                        </h3>
                        <p className="text-sm text-brand-gray mt-0.5">
                          {parts.join(' · ')}
                        </p>

                        {/* Metrics row — desktop */}
                        <div className="hidden sm:flex items-center gap-6 mt-3">
                          {/* Progress */}
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-brand-border rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${progressColor}`}
                                style={{ width: `${emp.avgProgress}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-white min-w-[36px]">{emp.avgProgress}%</span>
                          </div>

                          {/* Score */}
                          <div className="flex items-center gap-1.5 text-sm">
                            <Star className="w-4 h-4 text-brand-gray flex-shrink-0" />
                            <span className={`font-medium ${scoreStyle.color}`}>
                              {emp.avgScore != null ? emp.avgScore.toFixed(1) : '—'}
                            </span>
                          </div>

                          {/* Eval coverage */}
                          <div className="flex items-center gap-1.5 text-sm text-brand-gray">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            <span>{emp.scoredCount}/{emp.total} avaliados</span>
                          </div>
                        </div>

                        {/* Metrics row — mobile */}
                        <div className="flex sm:hidden items-center gap-3 mt-2">
                          <div className="flex items-center gap-1.5">
                            <div className="w-16 h-2 bg-brand-border rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${progressColor}`}
                                style={{ width: `${emp.avgProgress}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium text-white">{emp.avgProgress}%</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs">
                            <Star className="w-3 h-3 text-brand-gray" />
                            <span className={`font-medium ${scoreStyle.color}`}>
                              {emp.avgScore != null ? emp.avgScore.toFixed(1) : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Navigate indicator */}
                    <ChevronRight className="w-5 h-5 text-brand-gray flex-shrink-0 ml-4" />
                  </div>
                </Card>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

export default withPageProtection(ObjetivosIndividuaisPage, PAGES.PERFORMANCE_OBJETIVOS_INDIVIDUAIS)
